import { useCallback, useEffect } from "react";
import { useRecoilState } from "recoil";
import { pathPickProductCardViewState } from "@/shared/store/pathPickProductCardViewStore";
import {
  PathPickProductCardComparisonLayout,
  PathPickProductCardLayout,
  PathPickProductCardViewSettings,
} from "@/features/pathpick/types";
import {
  DEFAULT_PATH_PICK_PRODUCT_CARD_VIEW,
  PATH_PICK_PRODUCT_CARD_VIEW_STORAGE_KEY,
} from "@/features/pathpick/constants";

const LAYOUTS: PathPickProductCardComparisonLayout[] = ["side", "stack"];

// 저장값은 사용자가 손으로 고칠 수 있고 과거 스키마일 수도 있다 — 모르는 값은 기본값으로 떨어뜨린다.
const normalize = (value: unknown): PathPickProductCardViewSettings => {
  if (typeof value !== "object" || value === null) {
    return DEFAULT_PATH_PICK_PRODUCT_CARD_VIEW;
  }
  const stored = value as Partial<PathPickProductCardViewSettings>;

  return {
    showComparison:
      typeof stored.showComparison === "boolean"
        ? stored.showComparison
        : DEFAULT_PATH_PICK_PRODUCT_CARD_VIEW.showComparison,
    comparisonLayout:
      stored.comparisonLayout && LAYOUTS.includes(stored.comparisonLayout)
        ? stored.comparisonLayout
        : DEFAULT_PATH_PICK_PRODUCT_CARD_VIEW.comparisonLayout,
  };
};

const readStorage = (): PathPickProductCardViewSettings | undefined => {
  // 서버(SSR)에는 localStorage가 없으므로 접근하지 않는다.
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    const raw = window.localStorage.getItem(
      PATH_PICK_PRODUCT_CARD_VIEW_STORAGE_KEY
    );
    return raw === null ? undefined : normalize(JSON.parse(raw));
  } catch {
    return undefined;
  }
};

/**
 * 상품 카드 보기 설정 훅 — 상품 찾기와 담은 상품이 같은 값을 본다.
 *
 * SSR-safe: 첫 렌더는 항상 기본값으로 시작하고(서버/클라이언트 첫 렌더 일치), 마운트 후
 * effect에서 저장값을 복원한다. 여러 컴포넌트가 동시에 복원해도 같은 값을 넣으므로 멱등이다.
 */
const usePathPickProductCardView = () => {
  const [settings, setSettings] = useRecoilState(pathPickProductCardViewState);

  useEffect(() => {
    const stored = readStorage();
    if (stored === undefined) {
      return;
    }
    // 값이 같으면 이전 객체를 그대로 돌려준다 — 화면마다 이 훅이 여러 번 쓰이므로,
    // 마운트할 때마다 새 객체를 넣으면 같은 값인데도 구독자 전체가 다시 그려진다.
    setSettings((prev) =>
      prev.showComparison === stored.showComparison &&
      prev.comparisonLayout === stored.comparisonLayout
        ? prev
        : stored
    );
  }, [setSettings]);

  const save = useCallback(
    (next: PathPickProductCardViewSettings) => {
      setSettings(next);
      if (typeof window === "undefined") {
        return;
      }
      try {
        window.localStorage.setItem(
          PATH_PICK_PRODUCT_CARD_VIEW_STORAGE_KEY,
          JSON.stringify(next)
        );
      } catch {
        // 저장 실패(용량 초과/사생활 모드 등)는 무시한다 — 이번 세션에는 그대로 적용된다.
      }
    },
    [setSettings]
  );

  // 카드가 실제로 그릴 레이아웃. 비교 표시를 끄더라도 고른 배치는 설정에 남겨 둔다
  // (다시 켜면 이전 배치로 돌아온다).
  const layout: PathPickProductCardLayout = settings.showComparison
    ? settings.comparisonLayout
    : "none";

  return {
    settings,
    layout,
    isComparisonVisible: settings.showComparison,
    save,
  };
};

export default usePathPickProductCardView;
